import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Element, MODELES, THEME_SOMBRE, type ElementDessin, type TypeElement } from "../plan/dessin.js";
import { appel, type EvenementVue, type PlanStandVue } from "./api.js";
import { dire, useEnregistrement } from "./commun.js";

/**
 * Le plan du stand : on glisse les tables de l'événement et quelques
 * éléments (comptoir, écran, cloison, entrée, salon, plante) sur le sol du
 * stand. Le visiteur le voit à l'heure du rendez-vous, sa table en avant.
 *
 * Pensé pour quelqu'un qui n'a jamais dessiné de plan : on glisse, on lâche,
 * on déplace ; un bouton pour tourner, un pour supprimer, Ctrl+Z pour revenir.
 * Tout est en mètres et s'enregistre seul.
 */

interface ElementEdit {
  id: string;
  type: TypeElement;
  x: number;
  y: number;
  w: number;
  h: number;
  rot: number;
  tableId: string | null;
  label: string;
}

interface PlanEdit {
  largeurM: number;
  profondeurM: number;
  elements: ElementEdit[];
}

const PAS = 0.1;
const caler = (v: number): number => Math.round(v / PAS) * PAS;
const arrondi = (v: number): number => Math.round(v * 100) / 100;
const nouvelId = (): string => `el-${Math.random().toString(36).slice(2, 9)}`;
const AUTRES: TypeElement[] = ["comptoir", "ecran", "mur", "entree", "salon", "plante"];

type Geste =
  | { quoi: "deplacer"; id: string; dx: number; dy: number; bouge: boolean }
  | { quoi: "taille"; id: string; cx: number; cy: number; rot: number; rond: boolean }
  | { quoi: "poser"; type: TypeElement; tableId: string | null; x: number; y: number; bouge: boolean };

function depuisVue(p: PlanStandVue | undefined): PlanEdit {
  return {
    largeurM: p?.largeurM ?? 6,
    profondeurM: p?.profondeurM ?? 4,
    elements: (p?.elements ?? []).map((e) => ({ ...e })),
  };
}

export function PlanDuStand(props: { e: EvenementVue }): JSX.Element {
  const e = props.e;
  const [plan, setPlanEtat] = useState<PlanEdit>(() => depuisVue(e.plan));
  const [sel, setSel] = useState<string | null>(null);
  const [geste, setGeste] = useState<Geste | null>(null);
  const [etat, setEtat] = useState<"" | "envoi" | "ok">("");
  const historique = useRef<PlanEdit[]>([]);
  const svg = useRef<SVGSVGElement>(null);

  const envoyer = useEnregistrement<PlanEdit>(async (v) => {
    setEtat("envoi");
    try {
      await appel("/api/regie/evenements/courant", { method: "PATCH", corps: { plan: v } });
      setEtat("ok");
    } catch (err) {
      setEtat("");
      dire((err as Error).message || "Plan non enregistré.", true);
    }
  }, 700);

  const planRef = useRef(plan);
  planRef.current = plan;
  /** Un état du plan qui compte : celui d'avant gardé pour Ctrl+Z, le nouveau enregistré. */
  const appliquer = useCallback(
    (apres: PlanEdit, avant: PlanEdit | null): void => {
      if (avant) historique.current = [...historique.current.slice(-39), avant];
      planRef.current = apres;
      setPlanEtat(apres);
      envoyer(apres);
    },
    [envoyer],
  );
  const changer = useCallback((f: (p: PlanEdit) => PlanEdit): void => appliquer(f(planRef.current), planRef.current), [appliquer]);

  const annuler = useCallback((): void => {
    const avant = historique.current.pop();
    if (!avant) return;
    appliquer(avant, null);
  }, [appliquer]);

  const tables = useMemo(() => e.tables.slice().sort((a, b) => a.nom.localeCompare(b.nom, "fr", { numeric: true })), [e.tables]);
  const tableDe = (id: string | null) => (id ? tables.find((t) => t.id === id) : undefined);
  const dessin = (x: ElementEdit): ElementDessin => {
    const t = tableDe(x.tableId);
    return { ...x, label: t ? t.nom : x.label, couleur: t ? t.couleur : x.type === "table" ? "#6b6b6b" : null };
  };
  const choisi = plan.elements.find((x) => x.id === sel) ?? null;

  /** Un point de l'écran, en mètres sur le plan. */
  const enMetres = (clientX: number, clientY: number): { x: number; y: number } | null => {
    const s = svg.current;
    const m = s?.getScreenCTM();
    if (!s || !m) return null;
    const p = new DOMPoint(clientX, clientY).matrixTransform(m.inverse());
    return { x: p.x, y: p.y };
  };
  const dansLePlan = (clientX: number, clientY: number): boolean => {
    const r = svg.current?.getBoundingClientRect();
    return !!r && clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom;
  };

  /** Poser un élément (ou une table) centré sur un point ; une table déjà posée est seulement choisie. */
  const poser = (type: TypeElement, tableId: string | null, centre?: { x: number; y: number }): void => {
    if (tableId) {
      const deja = planRef.current.elements.find((x) => x.tableId === tableId);
      if (deja) {
        setSel(deja.id);
        if (!centre) return;
        changer((p) => ({ ...p, elements: p.elements.map((x) => (x.id === deja.id ? { ...x, x: caler(centre.x - x.w / 2), y: caler(centre.y - x.h / 2) } : x)) }));
        return;
      }
    }
    const m = MODELES[type];
    const c = centre ?? libre(planRef.current, m.w, m.h);
    const el: ElementEdit = { id: nouvelId(), type, x: arrondi(caler(c.x - m.w / 2)), y: arrondi(caler(c.y - m.h / 2)), w: m.w, h: m.h, rot: 0, tableId, label: "" };
    changer((p) => ({ ...p, elements: [...p.elements, el] }));
    setSel(el.id);
  };

  // Les gestes en cours suivent la souris (ou le doigt) sur toute la page.
  useEffect(() => {
    if (!geste) return;
    const bouger = (ev: PointerEvent): void => {
      if (geste.quoi === "poser") {
        setGeste({ ...geste, x: ev.clientX, y: ev.clientY, bouge: true });
        return;
      }
      const p = enMetres(ev.clientX, ev.clientY);
      if (!p) return;
      if (geste.quoi === "deplacer") {
        if (!geste.bouge) setGeste({ ...geste, bouge: true });
        setPlanEtat((pl) => ({
          ...pl,
          elements: pl.elements.map((x) => (x.id === geste.id ? { ...x, x: arrondi(caler(p.x - geste.dx)), y: arrondi(caler(p.y - geste.dy)) } : x)),
        }));
      } else {
        // La poignée suit le pointeur dans le repère de l'élément (même tourné) ; le centre reste en place.
        const a = (-geste.rot * Math.PI) / 180;
        const dx = p.x - geste.cx;
        const dy = p.y - geste.cy;
        const lx = Math.abs(dx * Math.cos(a) - dy * Math.sin(a));
        const ly = Math.abs(dx * Math.sin(a) + dy * Math.cos(a));
        let w = Math.max(0.1, arrondi(caler(lx * 2)));
        let h = Math.max(0.1, arrondi(caler(ly * 2)));
        if (geste.rond) w = h = Math.max(w, h);
        setPlanEtat((pl) => ({
          ...pl,
          elements: pl.elements.map((x) => (x.id === geste.id ? { ...x, w, h, x: arrondi(geste.cx - w / 2), y: arrondi(geste.cy - h / 2) } : x)),
        }));
      }
    };
    const lacher = (ev: PointerEvent): void => {
      if (geste.quoi === "poser") {
        if (geste.bouge && dansLePlan(ev.clientX, ev.clientY)) {
          const p = enMetres(ev.clientX, ev.clientY);
          if (p) poser(geste.type, geste.tableId, p);
        } else if (!geste.bouge) {
          poser(geste.type, geste.tableId);
        }
      } else if (geste.quoi === "deplacer" && !geste.bouge) {
        /* un simple clic : l'élément est choisi, rien ne bouge */
      } else {
        // Le geste est fini : on garde l'état d'avant pour Ctrl+Z, on enregistre.
        appliquer(planRef.current, avantGeste.current);
      }
      setGeste(null);
    };
    window.addEventListener("pointermove", bouger);
    window.addEventListener("pointerup", lacher);
    return () => {
      window.removeEventListener("pointermove", bouger);
      window.removeEventListener("pointerup", lacher);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geste]);
  const avantGeste = useRef<PlanEdit | null>(null);

  // Le clavier : supprimer, flèches, R pour tourner, Ctrl+Z.
  useEffect(() => {
    const touche = (ev: KeyboardEvent): void => {
      const cible = ev.target as HTMLElement | null;
      if (cible && (cible.tagName === "INPUT" || cible.tagName === "TEXTAREA" || cible.tagName === "SELECT")) return;
      if ((ev.ctrlKey || ev.metaKey) && ev.key.toLowerCase() === "z") {
        ev.preventDefault();
        annuler();
        return;
      }
      if (!sel) return;
      const pas = ev.shiftKey ? 0.5 : PAS;
      const dep: Record<string, [number, number]> = { ArrowLeft: [-pas, 0], ArrowRight: [pas, 0], ArrowUp: [0, -pas], ArrowDown: [0, pas] };
      if (dep[ev.key]) {
        ev.preventDefault();
        const [dx, dy] = dep[ev.key]!;
        changer((p) => ({ ...p, elements: p.elements.map((x) => (x.id === sel ? { ...x, x: arrondi(x.x + dx), y: arrondi(x.y + dy) } : x)) }));
      } else if (ev.key === "Delete" || ev.key === "Backspace") {
        ev.preventDefault();
        changer((p) => ({ ...p, elements: p.elements.filter((x) => x.id !== sel) }));
        setSel(null);
      } else if (ev.key.toLowerCase() === "r") {
        changer((p) => ({ ...p, elements: p.elements.map((x) => (x.id === sel ? { ...x, rot: (x.rot + 90) % 360 } : x)) }));
      } else if (ev.key === "Escape") {
        setSel(null);
      }
    };
    window.addEventListener("keydown", touche);
    return () => window.removeEventListener("keydown", touche);
  }, [sel, changer, annuler]);

  const maj = (id: string, f: (x: ElementEdit) => ElementEdit): void => changer((p) => ({ ...p, elements: p.elements.map((x) => (x.id === id ? f(x) : x)) }));

  const W = plan.largeurM;
  const H = plan.profondeurM;
  const marge = 0.6;
  const lignes: JSX.Element[] = [];
  for (let x = 0.5; x < W; x += 0.5) lignes.push(<line key={`x${x}`} x1={x} y1={0} x2={x} y2={H} className={Number.isInteger(x) ? "pl-grille fort" : "pl-grille"} />);
  for (let y = 0.5; y < H; y += 0.5) lignes.push(<line key={`y${y}`} x1={0} y1={y} x2={W} y2={y} className={Number.isInteger(y) ? "pl-grille fort" : "pl-grille"} />);

  const commencerPose = (ev: React.PointerEvent, type: TypeElement, tableId: string | null): void => {
    ev.preventDefault();
    setGeste({ quoi: "poser", type, tableId, x: ev.clientX, y: ev.clientY, bouge: false });
  };

  return (
    <>
      <div className="hd">
        <div>
          <h1>Plan du stand</h1>
        </div>
        <span className="sp" />
        <span className="sub" aria-live="polite">
          {etat === "envoi" ? "Enregistrement…" : etat === "ok" ? "Enregistré" : ""}
        </span>
        <button type="button" className="btn s q" disabled={historique.current.length === 0} onClick={annuler} title="Ctrl+Z">
          Annuler
        </button>
      </div>

      <div className="pl-atelier">
        <aside className="pl-palette carte">
          <span className="etiq">Tables</span>
          {tables.length === 0 ? <span className="sub">Créez d'abord une table.</span> : null}
          {tables.map((t) => {
            const posee = plan.elements.some((x) => x.tableId === t.id);
            return (
              <button
                key={t.id}
                type="button"
                className={`pl-objet${posee ? " posee" : ""}`}
                onPointerDown={(ev) => commencerPose(ev, "table", t.id)}
                title={posee ? "Déjà sur le plan : cliquez pour la retrouver" : "Glissez sur le plan"}
              >
                <i style={{ background: t.couleur, borderRadius: "50%" }} />
                <span>
                  <b>{t.nom}</b>
                  {t.sujet && t.sujet !== t.nom ? <small>{t.sujet}</small> : null}
                </span>
                {posee ? <span className="pl-coche" aria-label="sur le plan">✓</span> : null}
              </button>
            );
          })}
          <span className="etiq" style={{ marginTop: 10 }}>
            Éléments
          </span>
          <div className="pl-elements">
            {AUTRES.map((type) => (
              <button key={type} type="button" className="pl-objet petit" onPointerDown={(ev) => commencerPose(ev, type, null)}>
                <svg viewBox="-0.2 -0.2 2.4 1.6" aria-hidden="true">
                  <Element e={{ type, ...apercu(type), rot: 0, label: " " }} theme={THEME_SOMBRE} />
                </svg>
                <span>{MODELES[type].nom}</span>
              </button>
            ))}
          </div>
        </aside>

        <section className="pl-scene carte">
          <div className="pl-taille">
            <label>
              Largeur
              <input
                className="in"
                type="number"
                min={1}
                max={200}
                step={0.5}
                value={W}
                onChange={(ev) => {
                  const v = Number(ev.target.value);
                  if (v >= 1 && v <= 200) changer((p) => ({ ...p, largeurM: v }));
                }}
              />
              m
            </label>
            <label>
              Profondeur
              <input
                className="in"
                type="number"
                min={1}
                max={200}
                step={0.5}
                value={H}
                onChange={(ev) => {
                  const v = Number(ev.target.value);
                  if (v >= 1 && v <= 200) changer((p) => ({ ...p, profondeurM: v }));
                }}
              />
              m
            </label>
            <span className="sp" />
            {choisi ? (
              <Outils
                el={choisi}
                tables={tables}
                tableDe={tableDe}
                maj={(f) => maj(choisi.id, f)}
                dupliquer={() => {
                  const copie = { ...choisi, id: nouvelId(), x: arrondi(choisi.x + 0.3), y: arrondi(choisi.y + 0.3), tableId: null };
                  changer((p) => ({ ...p, elements: [...p.elements, copie] }));
                  setSel(copie.id);
                }}
                supprimer={() => {
                  changer((p) => ({ ...p, elements: p.elements.filter((x) => x.id !== choisi.id) }));
                  setSel(null);
                }}
              />
            ) : null}
          </div>
          <svg
            ref={svg}
            className="pl-svg"
            viewBox={`${-marge} ${-marge} ${W + 2 * marge} ${H + 2 * marge}`}
            onPointerDown={(ev) => {
              if (ev.target === ev.currentTarget || (ev.target as Element).classList?.contains("pl-sol")) setSel(null);
            }}
          >
            <rect className="pl-sol" x={0} y={0} width={W} height={H} rx={0.05} />
            {lignes}
            <text className="pl-cote" x={W / 2} y={-0.22} textAnchor="middle">
              {W.toLocaleString("fr-FR")} m
            </text>
            <text className="pl-cote" x={-0.22} y={H / 2} textAnchor="middle" transform={`rotate(-90 ${-0.22} ${H / 2})`}>
              {H.toLocaleString("fr-FR")} m
            </text>
            {plan.elements.map((x) => (
              <g
                key={x.id}
                className="pl-el"
                onPointerDown={(ev) => {
                  ev.stopPropagation();
                  const p = enMetres(ev.clientX, ev.clientY);
                  if (!p) return;
                  setSel(x.id);
                  avantGeste.current = plan;
                  setGeste({ quoi: "deplacer", id: x.id, dx: p.x - x.x, dy: p.y - x.y, bouge: false });
                }}
              >
                <Element e={dessin(x)} theme={THEME_SOMBRE} />
              </g>
            ))}
            {choisi ? <Selection el={choisi} onTaille={(ev) => {
              ev.stopPropagation();
              avantGeste.current = plan;
              setGeste({ quoi: "taille", id: choisi.id, cx: choisi.x + choisi.w / 2, cy: choisi.y + choisi.h / 2, rot: choisi.rot, rond: choisi.type === "table" ? Math.abs(choisi.w - choisi.h) < 0.05 : choisi.type === "plante" });
            }} /> : null}
          </svg>
          {plan.elements.length === 0 ? <p className="pl-vide">Glissez une table ou un élément sur le plan.</p> : null}
        </section>
      </div>

      {geste?.quoi === "poser" && geste.bouge ? (
        <div className="pl-fantome" style={{ left: geste.x, top: geste.y }}>
          {geste.tableId ? tableDe(geste.tableId)?.nom : MODELES[geste.type].nom}
        </div>
      ) : null}
    </>
  );
}

/** Le cadre de l'élément choisi et sa poignée de taille. */
function Selection(props: { el: ElementEdit; onTaille: (ev: React.PointerEvent) => void }): JSX.Element {
  const e = props.el;
  const cx = e.x + e.w / 2;
  const cy = e.y + e.h / 2;
  const m = 0.08;
  return (
    <g transform={`rotate(${e.rot} ${cx} ${cy})`} pointerEvents="none">
      <rect className="pl-cadre" x={e.x - m} y={e.y - m} width={e.w + 2 * m} height={e.h + 2 * m} />
      <rect className="pl-poignee" x={e.x + e.w + m - 0.11} y={e.y + e.h + m - 0.11} width={0.22} height={0.22} pointerEvents="all" onPointerDown={props.onTaille} />
    </g>
  );
}

/** La barre de l'élément choisi : table associée ou nom, tourner, dupliquer, supprimer. */
function Outils(props: {
  el: ElementEdit;
  tables: EvenementVue["tables"];
  tableDe: (id: string | null) => EvenementVue["tables"][number] | undefined;
  maj: (f: (x: ElementEdit) => ElementEdit) => void;
  dupliquer: () => void;
  supprimer: () => void;
}): JSX.Element {
  const e = props.el;
  return (
    <div className="pl-outils">
      {e.type === "table" ? (
        <select className="in" aria-label="Table" value={e.tableId ?? ""} onChange={(ev) => props.maj((x) => ({ ...x, tableId: ev.target.value || null }))}>
          <option value="">Table sans nom</option>
          {props.tables.map((t) => (
            <option key={t.id} value={t.id}>
              {t.nom}
            </option>
          ))}
        </select>
      ) : (
        <input className="in" aria-label="Nom" placeholder={MODELES[e.type].nom} maxLength={40} value={e.label} onChange={(ev) => props.maj((x) => ({ ...x, label: ev.target.value }))} />
      )}
      <span className="sub" style={{ whiteSpace: "nowrap" }}>
        {e.w.toLocaleString("fr-FR")} × {e.h.toLocaleString("fr-FR")} m
      </span>
      <button type="button" className="btn s q" title="R" onClick={() => props.maj((x) => ({ ...x, rot: (x.rot + 90) % 360 }))}>
        Tourner
      </button>
      <button type="button" className="btn s q" onClick={props.dupliquer}>
        Dupliquer
      </button>
      <button type="button" className="btn s q" title="Suppr" onClick={props.supprimer}>
        Supprimer
      </button>
    </div>
  );
}

/** L'aperçu d'un élément dans la palette, à l'échelle d'une vignette. */
function apercu(type: TypeElement): { x: number; y: number; w: number; h: number } {
  const m = MODELES[type];
  const k = Math.min(1.8 / m.w, 1 / m.h, 1.4);
  const w = m.w * k;
  const h = Math.max(0.1, m.h * k);
  return { x: 1 - w / 2, y: 0.6 - h / 2, w, h };
}

/** Une place libre où poser un élément cliqué (sans glisser) : en partant du haut à gauche. */
function libre(plan: PlanEdit, w: number, h: number): { x: number; y: number } {
  const occupe = (x: number, y: number): boolean =>
    plan.elements.some((e) => x < e.x + e.w + 0.2 && x + w + 0.2 > e.x && y < e.y + e.h + 0.2 && y + h + 0.2 > e.y);
  for (let y = 0.3; y + h <= plan.profondeurM; y += 0.5) {
    for (let x = 0.3; x + w <= plan.largeurM; x += 0.5) {
      if (!occupe(x, y)) return { x: x + w / 2, y: y + h / 2 };
    }
  }
  return { x: plan.largeurM / 2, y: plan.profondeurM / 2 };
}
